import React, { useMemo } from "react"
import { FormProvider } from "react-hook-form"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { WithClassName } from "shared/types/WithClassName"
import { FormProvider as BaseFormProvider } from "shared/ui/form/FormContext"

import { RoomTitle } from "entities/rooms/ui/room-title/RoomTitle"
import { RoomFormModel } from "features/room-management/types/RoomFormModel"

import { RoomFormAttachmentsProvider } from "./context/RoomFormAttachmentsContext"
import {
  RoomFormContextValue,
  RoomFormProvider
} from "./contexts/RoomFormContext"
import { useRoomFormViewModel } from "./model/useRoomFormViewModel"
import "./styles/_room-form.scss"
import { RoomFormClarificationRequest } from "./ui/RoomFormClarificationRequest"
import { RoomFormTabs } from "./ui/RoomFormTabs"
import { RoomAirVolumeInput } from "./ui/inputs/RoomAirVolumeInput"
import { RoomAirVolumePerAreaInput } from "./ui/inputs/RoomAirVolumePerAreaInput"
import { RoomAirVolumePerPersonInput } from "./ui/inputs/RoomAirVolumePerPersonInput"
import { RoomAreaInput } from "./ui/inputs/RoomAreaInput"
import { RoomExhaustAirInput } from "./ui/inputs/RoomExhaustAirInput"
import { RoomFloorInput } from "./ui/inputs/RoomFloorInput"
import { RoomFortluftInput } from "./ui/inputs/RoomFortluftInput"
import { RoomHeightInput } from "./ui/inputs/RoomHeightInput"
import { RoomLabelInput } from "./ui/inputs/RoomLabelInput"
import { RoomNumberInput } from "./ui/inputs/RoomNumberInput"
import { RoomOutsideAirInput } from "./ui/inputs/RoomOutsideAirInput"
import { RoomPersonsNumberInput } from "./ui/inputs/RoomPersonsNumberInput"
import { RoomPositionInput } from "./ui/inputs/RoomPositionInput"
import { RoomProjectInput } from "./ui/inputs/RoomProjectInput"
import { RoomRecirculatedAirInput } from "./ui/inputs/RoomRecirculatedAirInput"
import { RoomRemarkInput } from "./ui/inputs/RoomRemarkInput"
import { RoomRoomAirInput } from "./ui/inputs/RoomRoomAirInput"
import { RoomSecondaryAirInput } from "./ui/inputs/RoomSecondaryAirInput"
import { RoomSoundPressureLevelCustomInput } from "./ui/inputs/RoomSoundPressureLevelCustomInput"
import { RoomSoundPressureLevelPresetInput } from "./ui/inputs/RoomSoundPressureLevelPresetInput"
import { RoomSupplyAirInput } from "./ui/inputs/RoomSupplyAirInput"
import { RoomTransferAirInput } from "./ui/inputs/RoomTransferAirInput"
import { RoomUtilizationInput } from "./ui/inputs/RoomUtilizationInput"

export interface RoomFormProps extends RoomFormModel, WithClassName {
  id: string
  autoFocus?: boolean
  isReadonly?: boolean
  isDisabled?: boolean
  children?: React.ReactNode
}

const RoomFormRoot = React.memo((props: RoomFormProps) => {
  const {
    id,
    className,
    children,
    isReadonly,
    isDisabled,
    positionOptions,
    isLoadingPositionOptions,
    projectOptions,
    isLoadingProjectOptions,
    floorOptions,
    isLoadingFloorOptions,
    shouldShowHint,
    formOptions,
    onHintClose,
    clarificationRequest
  } = props

  const {
    hideProject = true,
    hideClarificationRequest,
    hideAttachments
  } = formOptions || {}

  const { t } = useTranslation()
  const { form, onSubmit, defaultTab, attachmentsContext } =
    useRoomFormViewModel(props)

  const contextValue: RoomFormContextValue = useMemo(
    () => ({
      isReadonly,
      isDisabled,
      positionOptions,
      isLoadingPositionOptions,
      projectOptions,
      isLoadingProjectOptions,
      floorOptions,
      isLoadingFloorOptions,
      hideProject,
      hideClarificationRequest,
      hideAttachments,
      shouldShowHint,
      onHintClose,
      clarificationRequest
    }),
    [
      isReadonly,
      isDisabled,
      positionOptions,
      isLoadingPositionOptions,
      projectOptions,
      isLoadingProjectOptions,
      floorOptions,
      isLoadingFloorOptions,
      hideProject,
      hideClarificationRequest,
      hideAttachments,
      shouldShowHint,
      onHintClose,
      clarificationRequest
    ]
  )

  return (
    <BaseFormProvider isReadonly={isReadonly} isDisabled={isDisabled}>
      <RoomFormProvider value={contextValue}>
        <RoomFormAttachmentsProvider value={attachmentsContext}>
          <FormProvider {...form}>
            <form
              id={id}
              onSubmit={onSubmit}
              aria-label={t("Room.Actions.Update")}
              className={classNames("form room-form", className)}
              data-testid="room-form"
            >
              {children || <RoomFormTabs defaultTab={defaultTab} />}
            </form>
          </FormProvider>
        </RoomFormAttachmentsProvider>
      </RoomFormProvider>
    </BaseFormProvider>
  )
})

export const RoomForm = Object.assign(RoomFormRoot, {
  Tabs: RoomFormTabs,
  ClarificationRequest: RoomFormClarificationRequest,
  Title: RoomTitle,
  NumberInput: RoomNumberInput,
  LabelInput: RoomLabelInput,
  FloorInput: RoomFloorInput,
  ProjectInput: RoomProjectInput,
  PositionInput: RoomPositionInput,
  UtilizationInput: RoomUtilizationInput,
  PersonsNumberInput: RoomPersonsNumberInput,
  AirVolumePerPersonInput: RoomAirVolumePerPersonInput,
  HeightInput: RoomHeightInput,
  AreaInput: RoomAreaInput,
  AirVolumePerAreaInput: RoomAirVolumePerAreaInput,
  AirVolumeInput: RoomAirVolumeInput,
  SupplyAirInput: RoomSupplyAirInput,
  ExhaustAirInput: RoomExhaustAirInput,
  TransferAirInput: RoomTransferAirInput,
  SecondaryAirInput: RoomSecondaryAirInput,
  RecirculatedAirInput: RoomRecirculatedAirInput,
  RoomAirInput: RoomRoomAirInput,
  OutsideAirInput: RoomOutsideAirInput,
  FortluftInput: RoomFortluftInput,
  RemarkInput: RoomRemarkInput,
  SoundPressureLevelPresetInput: RoomSoundPressureLevelPresetInput,
  SoundPressureLevelCustomInput: RoomSoundPressureLevelCustomInput
})
